import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import type { DialogProps } from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Modal from '@mui/material/Modal';
import type { PaperProps } from '@mui/material/Paper';
import Paper from '@mui/material/Paper';
import type { SxProps, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';

import { ModalSizeE } from 'declarations/enums/modal';
import type { ScrollLockIdE } from 'declarations/enums/scroll';
import useScrollLock from 'hooks/common/useScrollLock';

export interface DragAndResizeModalProps {
  open: boolean;
  handleClose: () => void;
  children?: ReactNode;
  modalText?: string;
  modalTitle?: string | ReactNode;
  size?: ModalSizeE;
  popupId?: ScrollLockIdE;
  disableEscapeKeyDown?: boolean;
  dialogStyles?: SxProps<Theme>;
  dialogContentStyles?: SxProps<Theme>;
  secondaryText?: string;
  infoTooltip?: string | ReactNode;
  dynamicHeight?: boolean;
  showCloseButton?: boolean;
  width?: number;
  height?: number;
  contentGrowsWithContent?: boolean;
  validationErrorCount?: number;
  isMobile?: boolean;
}

const HEADER_HEIGHT = 72;
const DEFAULT_WIDTH = 925;
const DEFAULT_HEIGHT = 600;
const MOBILE_MARGIN = 16;
const DESKTOP_WIDTH_RATIO = 0.9;
const WIDER_MODAL_WIDTH_RATIO = 0.98;

const getScreenConstraints = (
  isMobile?: boolean,
  useWiderWidth?: boolean
): { width: number; height: number } => {
  if (typeof window === 'undefined') return { width: 1920 * 0.9, height: 1080 * 0.95 };
  if (isMobile) {
    return {
      width: window.innerWidth - MOBILE_MARGIN * 2,
      height: window.innerHeight - MOBILE_MARGIN * 2,
    };
  }
  const widthRatio = useWiderWidth ? WIDER_MODAL_WIDTH_RATIO : DESKTOP_WIDTH_RATIO;
  return {
    width: window.innerWidth * widthRatio,
    height: window.innerHeight * 0.95,
  };
};

const SIZE_MAP: Partial<Record<ModalSizeE, { width: number; height: number }>> = {
  [ModalSizeE.XS]: { width: 500, height: 400 },
  [ModalSizeE.SM]: { width: 840, height: 455 },
  [ModalSizeE.MD]: { width: 925, height: 600 },
  [ModalSizeE.LG]: { width: 1350, height: 860 },
  [ModalSizeE.XL]: { width: 1540, height: 1000 },
  [ModalSizeE.TALL]: { width: 800, height: 1200 },
  [ModalSizeE.TICKET_DETAIL]: { width: 1100, height: 600 },
  [ModalSizeE.COLUMN_PICKER]: { width: 1550, height: 240 },
  [ModalSizeE.COLUMN_PICKER_SCHEDULE]: { width: 1250, height: 240 },
  [ModalSizeE.REASSIGN]: { width: 340, height: 340 },
  [ModalSizeE.CLOSE_TICKET]: { width: 340, height: 260 },
  [ModalSizeE.ATTACHMENTS]: { width: 600, height: 500 },
  [ModalSizeE.ADD_ATTACHMENT]: { width: 400, height: 420 },
  [ModalSizeE.DELETE_ATTACHMENT]: { width: 320, height: 200 },
  [ModalSizeE.APPROVE_ATTACHMENT]: { width: 320, height: 240 },
  [ModalSizeE.REQUEST_OVERRIDE_APPROVAL]: { width: 340, height: 260 },
  [ModalSizeE.IMPORT_SCHEDULE]: { width: 360, height: 300 },
  [ModalSizeE.VIEW_SCHEDULE_FILES]: { width: 564, height: 400 },
  [ModalSizeE.SEND_RO]: { width: 500, height: 400 },
};

const ResizableBoxStyled = styled(ResizableBox)(({ theme }) => ({
  position: 'relative',
  '& .react-resizable-handle': {
    position: 'absolute',
    width: 20,
    height: 20,
    bottom: 0,
    right: 0,
    cursor: 'se-resize',
    zIndex: 2,
    color: theme.palette.text.primary,
  },
  '& .react-resizable-handle::after': {
    content: '""',
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 16,
    height: 16,
    borderRight: '2px solid currentColor',
    borderBottom: '2px solid currentColor',
    borderBottomRightRadius: '12px',
    boxSizing: 'border-box',
  },
  maxHeight: '90%',
}));

type ResizeCallbackData = {
  node: HTMLElement;
  size: { width: number; height: number };
  handle: string;
};

const PaperComponentDraggableAndResizable = (
  props: PaperProps & {
    width: number;
    height: number;
    dynamicHeight?: boolean;
    onResizeStop?: (_e: unknown, data: ResizeCallbackData) => void;
  }
): JSX.Element => {
  const { width, height, dynamicHeight, onResizeStop, ...rest } = props;
  const nodeRef = useRef<HTMLDivElement>(null);

  if (dynamicHeight) {
    return (
      <Draggable
        nodeRef={nodeRef}
        handle="#draggable-dialog-title"
        cancel={'.react-resizable-handle, [class*="MuiDialogContent-root"]'}
        defaultPosition={{ x: 0, y: 0 }}
      >
        <div ref={nodeRef} style={{ display: 'inline-block', width }}>
          <Paper
            {...rest}
            sx={{
              width: '100%',
              minHeight: height,
              maxHeight: '90vh',
              boxShadow: (theme): string => theme.shadows[6],
              backgroundColor: (theme): string =>
                (theme.palette.background as { paperElevation0?: string })?.paperElevation0 ||
                theme.palette.background.paper,
              borderRadius: '8px',
              overflow: 'hidden',
            }}
            elevation={2}
          />
        </div>
      </Draggable>
    );
  }

  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'.react-resizable-handle, [class*="MuiDialogContent-root"]'}
      defaultPosition={{ x: 0, y: 0 }}
    >
      <div ref={nodeRef} style={{ display: 'inline-block' }}>
        <ResizableBoxStyled width={width} height={height} onResizeStop={onResizeStop}>
          <Paper
            {...rest}
            sx={{
              height: '100% !important',
              maxHeight: '100% !important',
              boxShadow: (theme): string => theme.shadows[6],
              backgroundColor: (theme): string =>
                (theme.palette.background as { paperElevation0?: string })?.paperElevation0 ||
                theme.palette.background.paper,
              borderRadius: '8px',
              overflow: 'hidden',
            }}
            elevation={2}
          />
        </ResizableBoxStyled>
      </div>
    </Draggable>
  );
};

const DragAndResizeModal: FC<DragAndResizeModalProps> = ({
  open,
  handleClose,
  modalText,
  modalTitle,
  children,
  size = ModalSizeE.MD,
  popupId,
  dialogStyles,
  dialogContentStyles,
  secondaryText,
  infoTooltip,
  dynamicHeight = false,
  showCloseButton = true,
  width: widthOverride,
  height: heightOverride,
  contentGrowsWithContent = false,
  validationErrorCount,
  isMobile = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { enableScroll, disableScroll } = useScrollLock();

  const useWiderWidth =
    size === ModalSizeE.COLUMN_PICKER || size === ModalSizeE.COLUMN_PICKER_SCHEDULE;

  const [modalSize, setModalSize] = useState(() => {
    const base = size != null ? SIZE_MAP[size] : undefined;
    const { width: maxW, height: maxH } = getScreenConstraints(isMobile, useWiderWidth);
    return {
      width: Math.min(widthOverride ?? base?.width ?? DEFAULT_WIDTH, maxW),
      height: Math.min(heightOverride ?? base?.height ?? DEFAULT_HEIGHT, maxH),
    };
  });

  useEffect(() => {
    const base = size != null ? SIZE_MAP[size] : undefined;
    const { width: maxW, height: maxH } = getScreenConstraints(isMobile, useWiderWidth);
    setModalSize({
      width: Math.min(widthOverride ?? base?.width ?? DEFAULT_WIDTH, maxW),
      height: Math.min(heightOverride ?? base?.height ?? DEFAULT_HEIGHT, maxH),
    });
  }, [size, widthOverride, heightOverride, isMobile, useWiderWidth]);

  useEffect(() => {
    if (!open || validationErrorCount === undefined || dynamicHeight) return;
    const el = contentRef.current;
    if (!el) return;
    const measureAndResize = (): void => {
      const scrollHeight = el.scrollHeight;
      const base = size != null ? SIZE_MAP[size] : undefined;
      const defaultHeight = heightOverride ?? base?.height ?? DEFAULT_HEIGHT;
      const neededHeight = HEADER_HEIGHT + scrollHeight;
      const { height: maxH } = getScreenConstraints(isMobile, useWiderWidth);
      const newHeight = Math.min(Math.max(neededHeight, defaultHeight), maxH);
      setModalSize(prev => ({ ...prev, height: newHeight }));
    };
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(measureAndResize);
    });
    return (): void => {
      cancelAnimationFrame(raf);
    };
  }, [open, validationErrorCount, dynamicHeight, size, heightOverride, isMobile, useWiderWidth]);

  const handleResizeStop = (_e: unknown, data: ResizeCallbackData): void => {
    const { width: w, height: h } = data.size;
    const { width: maxW, height: maxH } = getScreenConstraints(isMobile, useWiderWidth);
    setModalSize(prev => ({
      ...prev,
      width: Math.min(w, maxW),
      height: Math.min(h, maxH),
    }));
  };

  useEffect(() => {
    if (open && popupId) {
      disableScroll(popupId);
    }
  }, [open, popupId, disableScroll]);

  const handleMouseEnter = (): void => setIsHovered(true);
  const handleMouseLeave = (): void => setIsHovered(false);

  const closeHandler: DialogProps['onClose'] = (_e, reason) => {
    if (reason !== 'backdropClick') {
      setIsHovered(false);
      if (popupId) {
        enableScroll(popupId);
      }
      handleClose();
    }
  };

  const handleCloseModal = (): void => {
    setIsHovered(false);
    if (popupId) {
      enableScroll(popupId);
    }
    handleClose();
  };

  const showHeader = modalTitle != null || showCloseButton;

  return (
    <Modal
      open={open}
      onClose={closeHandler}
      hideBackdrop
      disableScrollLock
      aria-labelledby="draggable-dialog-title"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& *': {
          outline: 'none !important',
        },
        ...dialogStyles,
      }}
    >
      <Box>
        <PaperComponentDraggableAndResizable
          width={modalSize.width}
          height={modalSize.height}
          dynamicHeight={dynamicHeight}
          onResizeStop={handleResizeStop}
        >
          {showHeader && (
            <Box
              id="draggable-dialog-title"
              sx={{
                cursor: 'move',
                backgroundColor: isHovered ? '#4271EB80' : 'transparent',
                transition: 'background-color 0.3s ease',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {modalTitle != null && <Typography variant="h6">{modalTitle}</Typography>}
                {secondaryText != null && secondaryText !== '' && (
                  <Typography
                    sx={{
                      color: (theme: Theme) => theme.palette.success.main,
                      fontSize: '1.25rem',
                      fontWeight: 500,
                    }}
                  >
                    {secondaryText}
                  </Typography>
                )}
                {infoTooltip != null && infoTooltip !== '' && (
                  <Tooltip title={infoTooltip} arrow>
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: '1.25rem',
                        color: (theme: Theme) => theme.palette.text.secondary,
                        cursor: 'help',
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              {showCloseButton && (
                <Tooltip title="Close [ Esc ]" arrow>
                  <IconButton onClick={handleCloseModal}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          <Box
            ref={contentRef}
            sx={{
              width: '100%',
              height: showHeader ? `calc(100% - ${HEADER_HEIGHT}px)` : '100%',
              padding: 0,
              overflowY: 'auto',
              ...dialogContentStyles,
            }}
          >
            <DialogContent
              sx={{
                padding: 0,
                height: contentGrowsWithContent ? 'fit-content' : '100%',
                minHeight: contentGrowsWithContent ? '100%' : undefined,
                ...dialogContentStyles,
              }}
            >
              {modalText != null && modalText !== '' && (
                <DialogContentText>{modalText}</DialogContentText>
              )}
              {children}
            </DialogContent>
          </Box>
        </PaperComponentDraggableAndResizable>
      </Box>
    </Modal>
  );
};

export default DragAndResizeModal;
